import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Router } from '@angular/router';
import { HelperStateService } from '../../../services/helperState.service';
import { DASHBOARD_TOUR_KEY, TourService } from '../tour/tour.service';
import { findPageTour } from '../tour/page-tours';

interface VwFaqEntry {
  question: string;
  answer: string;
}

// Human-readable labels for helper keys the "tips you hid" list can offer to
// restore. Falls back to the raw key for anything not listed here, so a new
// tooltip/tour still shows up (just less prettily) without needing a code
// change here.
const HELPER_LABELS: { [key: string]: string } = {
  [DASHBOARD_TOUR_KEY]: 'Guided dashboard tour',
  'tour.guest-list': 'Visitor list tour',
  'tour.user-list': 'User Details tour',
  'tooltip.panic-alert': 'Panic alerts tip',
  'tooltip.dashboard.property-filter': 'Property filter tip',

  // Administrator / Flagged / Blacklist / Rejected
  'tour.group-list': 'User groups tour',
  'tour.configuration': 'Configuration tour',
  'tour.hierarchy-setup': 'Property hierarchy tour',
  'tour.reset-password-request': 'Password reset requests tour',
  'tour.entry-point': 'Entry point mapping tour',
  'tour.gate-master': 'Gates tour',
  'tour.device-binding': 'Device binding tour',
  'tour.registration-code': 'Registration codes tour',
  'tour.registration-request': 'Registration requests tour',
  'tour.flagged': 'Flagged visitors tour',
  'tour.unflagged': 'Unflagged visitors tour',
  'tour.blacklist': 'Blacklist tour',
  'tour.blacklist-history': 'Blacklist history tour',
  'tour.rejected-visitor': 'Rejected visitors tour',

  // OfficeStaff / household staff / HouseHelp / ScrapAgent
  'tour.office-staff': 'Staff list tour',
  'tour.office-current-staff': 'Current staff tour',
  'tour.office-staff-history': 'Staff history tour',
  'tour.office-registered-staff': 'Registered staff tour',
  'tour.office-expected-staff': 'Scheduled staff tour',
  'tour.office-staff-import': 'Staff import tour',
  'tour.household-staff': 'Domestic staff tour',
  'tour.household-current-staff': 'Domestic staff on-site tour',
  'tour.household-staff-history': 'Domestic staff history tour',
  'tour.household-expected-staff': 'Scheduled domestic staff tour',
  'tour.household-staff-import': 'Domestic staff import tour',
  'tour.house-help': 'House help tour',
  'tour.house-help-trespasser': 'Trespasser house help tour',
  'tour.scrap-agent': 'Scrap agents tour',

  // ServiceProvider / employee
  'tour.service-provider': 'Service provider list tour',
  'tour.booked-service-provider': 'Booked service providers tour',
  'tour.trespasser-service-provider': 'Trespasser service providers tour',
  'tour.recurrent-service-provider': 'Recurring service providers tour',
  'tour.recurrent-sp-dtl': 'Recurring service provider history tour',
  'tour.host-recurrent-sp': 'Your recurring service providers tour',
  'tour.employee': 'Staff list tour',
  'tour.employee-current-visitor': 'Current visitors tour',
  'tour.employee-expected-visitor': 'Booked visitors tour',
  'tour.employee-visitor-history': 'Visitor history tour',
  'tour.employee-import': 'Staff import tour',
  'tour.employee-import-guest': 'Guest import tour',

  // Guest / visitor
  'tour.recurrent-guest': 'Recurring visitors tour',
  'tour.booked-guest': 'Booked visitors tour',
  'tour.trespasser-guest': 'Trespasser visitors tour',
  'tour.guest-details': 'Visitor details tour',
  'tour.current-guest': 'Current visitors tour',
  'tour.host-recurrent-guest': 'Your recurring visitors tour',
  'tour.recurrent-guest-dtl': 'Recurring visitor history tour',
  'tour.self-check-in-generate': 'Self check-in QR tour',

  // Resident
  'tour.resident-list': 'Resident list tour',
  'tour.resident-family-list': 'Family members tour',
  'tour.expected-service-provider-resident': 'Booked service providers tour',
  'tour.current-service-provider-resident': 'Service providers on-site tour',
  'tour.service-provider-history-resident': 'Service provider history tour',
  'tour.resident-details': 'Resident details tour',
  'tour.expected-resident': 'Scheduled residents tour',
  'tour.current-resident': 'Residents on-site tour',
  'tour.resident-history': 'Resident history tour',
  'tour.vehicle-info-resident': 'Registered vehicles tour',
  'tour.self-service-link': 'Self-service link tour',
  'tour.self-guest-details': 'Self-registered guests tour',

  // Reports / premise / notifications / panic / vehicle / account
  'tour.exited-visitor': 'Exited visitor report tour',
  'tour.audit-trial-report': 'Audit trail report tour',
  'tour.visitor-vehicle-report': 'Vehicle report tour',
  'tour.daily-guest-report': 'Daily guest report tour',
  'tour.hod-list': 'Heads of department tour',
  'tour.dynamic-report': 'Dynamic report tour',
  'tour.society': 'Property records tour',
  'tour.check-in-notification': 'Check-in notifications tour',
  'tour.panic-notification': 'Panic notifications tour',
  'tour.vehicle-list': 'Vehicle records tour',
  'tour.vehicle-detail': 'Vehicle detail tour',
  'tour.expected-vehicle': 'Booked vehicles tour',
  'tour.current-vehicle': 'Vehicles on premises tour',
  'tour.import-vehicle': 'Vehicle import tour',
  'tour.profile': 'Profile tour',
  'tour.ocr-api-key': 'OCR API key tour',
  'tour.ocr-stats': 'OCR usage tour',
  'tour.mobile-binding-license': 'Mobile binding license tour',
};

// Bump this whenever WHATS_NEW_ENTRIES changes — a new key has no dismiss
// record for anyone yet, so it naturally reappears for every user once,
// reusing the same per-user helper-state mechanism as tours/tooltips
// instead of a separate "seen this release" system.
export const WHATS_NEW_KEY = 'whatsnew.v1';

interface VwWhatsNewEntry {
  title: string;
  items: string[];
}

const WHATS_NEW_ENTRIES: VwWhatsNewEntry[] = [
  {
    title: 'This update',
    items: [
      'Flagged/Blacklisted/Rejected Visitor now nest under Visitors in the sidebar, keeping their own sub-pages.',
      'Reports: quick date presets (Today/This Week/This Month), export progress on the Export button, and less wasted space in the filter row.',
      'Command palette (Ctrl/Cmd+K) now remembers pages you actually visit, not just search terms.',
      'Breadcrumbs: hover a segment to jump to a sibling page without leaving the one you\'re on.',
      'A countdown warning before you\'re signed out for inactivity, instead of being logged out with no notice.',
    ],
  },
];

const FAQS: VwFaqEntry[] = [
  {
    question: 'How do I check a visitor in or out?',
    answer: "Open Visitors in the sidebar. Use Booked Visitor to check in someone already expected, or add a walk-in from Current Visitor. When they leave, check them out from the Current Visitor list.",
  },
  {
    question: 'Where can I see who is on the property right now?',
    answer: "The dashboard's \"Current Guests\"/\"Current Visitors\" card shows a live count — click it to open the full Current Visitor list.",
  },
  {
    question: 'How do I switch between light and dark mode?',
    answer: 'Use the sun/moon icon in the top header, next to the Reports icon. Your choice is remembered on this device.',
  },
  {
    question: 'How do I register a new staff member or service provider?',
    answer: "Under Office Staff (or Service Provider) in the sidebar, use \"Add Staff\" / \"Add Service Provider\". Once saved, they appear in the matching list and can be checked in like any visitor.",
  },
  {
    question: 'What happens when I flag a visitor?',
    answer: 'Flagged visitors move to the Flagged screen and are blocked from a normal check-in until an admin reviews and unflags them.',
  },
  {
    question: 'How do I get notified about panic alerts?',
    answer: 'Account and Web Admins see a bell icon in the header with a live badge count. Clicking it opens Panic Notifications and clears the badge.',
  },
  {
    question: 'How do I filter the Exited Visitor/Vehicle Report on the dashboard?',
    answer: 'Pick a From/To date and a property in the Exited Visitor or Vehicle Report card, then click "Show Graph".',
  },
  {
    question: 'Can I bind a mobile device to my account?',
    answer: '"Bind Mobile App" appears at the bottom of the sidebar (and under Administrator > Device Binding Management) once that feature is enabled for your account.',
  },
];

@Component({
  selector: 'app-help-panel',
  templateUrl: './help-panel.component.html',
  styleUrls: ['./help-panel.component.scss'],
})
export class HelpPanelComponent {
  @Input() open = false;
  @Output() openChange = new EventEmitter<boolean>();

  faqs = FAQS;
  whatsNewEntries = WHATS_NEW_ENTRIES;

  constructor(private helperState: HelperStateService, private tour: TourService, private router: Router) {}

  get whatsNewDismissed(): boolean {
    return this.helperState.isDismissed(WHATS_NEW_KEY);
  }

  dismissWhatsNew(): void {
    this.helperState.markSeen(WHATS_NEW_KEY, true).subscribe();
  }

  get hiddenTips(): { key: string; label: string }[] {
    return this.helperState.getDontShowAgainKeys().map(key => ({ key, label: HELPER_LABELS[key] || key }));
  }

  // Only the current page's tour is offered — replaying the dashboard tour
  // from, say, the User Details screen would spotlight elements that don't
  // exist there. See shared/ui/tour/page-tours.ts for the route→tour map.
  get currentPageTourAvailable(): boolean {
    return !!findPageTour(this.router.url);
  }

  close(): void {
    this.open = false;
    this.openChange.emit(false);
  }

  replayTour(): void {
    const entry = findPageTour(this.router.url);
    if (!entry) {
      return;
    }
    this.close();
    setTimeout(() => this.tour.start(entry.steps, entry.key), 200);
  }

  restoreTip(key: string): void {
    this.helperState.reset(key).subscribe();
  }
}
